import { PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS, type ProductStatus } from '@navicosa/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Image, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import {
  type Category,
  PRICING_MODE_LABEL,
  type ProductListItem,
  STATUS_LABEL,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

const STATUS_COLOR: Record<ProductStatus, string> = {
  draft: 'default',
  published: 'green',
  archived: 'orange',
};

export function ProductListPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  // Giữ bộ lọc trên URL → quay lại từ trang chi tiết vẫn còn bộ lọc.
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get('q') ?? '');
  const page = Number(sp.get('page') ?? 1);
  const status = sp.get('status') ?? undefined;
  const category_id = sp.get('category_id') ?? undefined;
  const set = (k: string, v?: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k !== 'page') next.delete('page');
    setSp(next);
  };

  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/admin/categories'),
  });
  const list = useQuery({
    queryKey: ['products', page, status, category_id, sp.get('q')],
    queryFn: () =>
      api.page<ProductListItem>('/admin/products', {
        page,
        limit: 20,
        status,
        category_id,
        q: sp.get('q'),
      }),
  });

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Sản phẩm & giá
        </Typography.Title>
      }
      extra={
        can(PERMISSIONS.CATALOG_MANAGE) && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => nav('/products/new')}>
            Thêm sản phẩm
          </Button>
        )
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Tìm tên, model, mã (không cần dấu)"
          allowClear
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onSearch={(v) => set('q', v.trim() || undefined)}
          style={{ width: 300 }}
        />
        <Select
          allowClear
          placeholder="Danh mục"
          style={{ width: 260 }}
          value={category_id}
          onChange={(v) => set('category_id', v)}
          options={(cats.data ?? []).map((c) => ({
            value: c.id,
            label: c.parent_id ? `— ${c.name}` : c.name,
          }))}
        />
        <Select
          allowClear
          placeholder="Trạng thái"
          style={{ width: 180 }}
          value={status}
          onChange={(v) => set('status', v)}
          options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
        />
      </Space>
      <Table<ProductListItem>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items}
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.meta.total,
          onChange: (p) => set('page', String(p)),
          showTotal: (t) => `${t} sản phẩm`,
        }}
        locale={{ emptyText: 'Chưa có sản phẩm' }}
        columns={[
          {
            title: '',
            width: 64,
            render: (_, r) =>
              r.images[0] ? (
                // Khung dọc 3:4 như trên Mini App, hiện trọn xe
                <Image
                  src={r.images[0].file_url}
                  width={42}
                  height={56}
                  style={{ objectFit: 'contain', background: '#fff', borderRadius: 4 }}
                  preview={false}
                />
              ) : (
                <div style={{ width: 42, height: 56, background: '#f0f0f0', borderRadius: 4 }} />
              ),
          },
          {
            title: 'Sản phẩm',
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Link to={`/products/${r.id}`}>{r.name}</Link>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.product_code}
                  {r.model ? ` · ${r.model}` : ''}
                </Typography.Text>
              </Space>
            ),
          },
          { title: 'Danh mục', render: (_, r) => r.category.name },
          { title: 'Chế độ giá', render: (_, r) => PRICING_MODE_LABEL[r.pricing_mode] },
          { title: 'Số mức giá', align: 'right', render: (_, r) => r._count.prices },
          {
            title: 'Trạng thái',
            render: (_, r) => (
              <Space size={4} wrap>
                <Tag color={STATUS_COLOR[r.status]}>{STATUS_LABEL[r.status]}</Tag>
                {r.is_demo && <Tag color="purple">DEMO</Tag>}
                {r.is_featured && <Tag color="gold">Nổi bật</Tag>}
                {!r.accepting_requests && <Tag color="red">Tạm ngừng</Tag>}
              </Space>
            ),
          },
        ]}
      />
    </Card>
  );
}
